import { useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { SiteLayout } from '@/components/site/SiteLayout'
import { AuthCard } from '@/components/auth/AuthCard'
import { AuthForm, Field } from '@/components/auth/AuthForm'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { PhoneField } from '@/components/auth/PhoneField'
import { PasswordStrengthMeter } from '@/components/auth/PasswordStrengthMeter'
import { checkPasswordStrength } from '@/lib/password-strength'
import { useAuth } from '@/lib/auth'

const MIN_PASSWORD_LENGTH = 8

/** Same `next=` validation as SignInPage — only accept same-origin paths. */
function safeNextPath(rawNext: string | null): string {
  if (!rawNext) return '/account'
  if (rawNext.startsWith('/') && !rawNext.startsWith('//')) return rawNext
  return '/account'
}

export function SignUpPage() {
  const { signUp, session, loading } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const nextPath = safeNextPath(searchParams.get('next'))

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    if (firstName.trim().length === 0 || lastName.trim().length === 0) {
      setError('First name and last name are required.')
      return
    }
    if (phone.trim() && !isValidPhoneNumber(phone.trim())) {
      setError('Phone number is not valid.')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }
    if (!checkPasswordStrength(password).acceptable) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`)
      return
    }
    setSubmitting(true)
    const { error: signUpError, autoSignedIn } = await signUp(email, password, {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      phone: phone.trim() || undefined,
      company_name: companyName.trim() || undefined,
    })
    setSubmitting(false)
    if (signUpError) {
      setError(signUpError.message)
      return
    }
    // Universal post-signup destination: /verify-email. Anti-enumeration
    // means we can't tell whether the email was new or a duplicate,
    // so we always send the user to "check your email" — the page has
    // a Resend button and an "Already verified? Sign in" link for the
    // duplicate-confirmed case. If a session was returned (confirmations
    // disabled), skip straight to the protected destination.
    //
    // Email is handed off via sessionStorage rather than a URL query
    // param to keep PII out of browser history, screenshots, server
    // logs, and Referer headers. sessionStorage scopes to this tab and
    // clears when the tab closes — the right lifecycle for an
    // ephemeral signup flow, and it still survives manual refresh.
    if (autoSignedIn) {
      navigate(nextPath, { replace: true })
    } else {
      try {
        sessionStorage.setItem('signup-pending-email', email)
      } catch {
        // Storage access can throw in private-mode Safari; the page
        // degrades gracefully to a generic "we sent a link" message.
      }
      navigate('/verify-email', { replace: true })
    }
  }

  // Already signed in? No reason to be on sign-up — go to where they were
  // headed (next param honored) or /account by default.
  if (!loading && session) {
    return <Navigate to={nextPath} replace />
  }

  return (
    <SiteLayout>
      <AuthCard
        title="Create Your Account"
        subtitle="Generate your own GeoBorder API keys and track usage."
        footer={
          <>
            Already have an account?{' '}
            <Link
              to={
                searchParams.get('next')
                  ? `/sign-in?next=${encodeURIComponent(searchParams.get('next') ?? '')}`
                  : '/sign-in'
              }
              className="text-[var(--color-accent)] underline underline-offset-2"
            >
              Sign in
            </Link>
            .
          </>
        }
      >
        <AuthForm
          onSubmit={onSubmit}
          submitLabel="Create Account"
          submitting={submitting}
          error={error}
        >
          {/* Two-column on wider viewports keeps the form compact. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field
              id="first-name"
              label="First name"
              type="text"
              value={firstName}
              onChange={setFirstName}
              autoComplete="given-name"
            />
            <Field
              id="last-name"
              label="Last name"
              type="text"
              value={lastName}
              onChange={setLastName}
              autoComplete="family-name"
            />
          </div>
          <Field
            id="company-name"
            label="Company"
            type="text"
            value={companyName}
            onChange={setCompanyName}
            autoComplete="organization"
            required={false}
            hint="Optional"
            placeholder="Becomes your organization name"
          />
          <PhoneField
            id="phone"
            label="Phone"
            value={phone}
            onChange={setPhone}
            required={false}
            hint="Optional"
          />
          <Field
            id="email"
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="email"
            placeholder="you@example.com"
          />
          <div className="flex flex-col gap-1">
            <Field
              id="password"
              label="Password"
              type="password"
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              minLength={MIN_PASSWORD_LENGTH}
            />
            <PasswordStrengthMeter password={password} />
          </div>
          <Field
            id="confirm-password"
            label="Confirm password"
            type="password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            // Real-time match feedback. Stays quiet until the user has
            // typed something in this field so the form doesn't yell
            // about a mismatch before there's anything to compare.
            feedback={
              confirmPassword.length === 0
                ? null
                : password === confirmPassword
                  ? { kind: 'ok', text: 'Passwords match.' }
                  : { kind: 'err', text: "Passwords don't match." }
            }
          />
        </AuthForm>
      </AuthCard>
    </SiteLayout>
  )
}
